import React, { useState } from "react";
import { CommandItem } from "@/components/ui/command";
import { StatusBadge } from "@/components/ui/status-badge";
import { StatusBadge as TaskStatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  getIconGlyphClasses,
  getIconShellClasses,
  type IconShellSemantic,
} from "@/lib/icon-shell";
import {
  User,
  Home,
  MessageSquare,
  CheckSquare,
  FolderKanban,
  DollarSign,
  Wallet,
  Package,
  Target,
  HandHeart,
  Calendar,
  Search,
  Navigation,
  Plus,
  Zap,
  Settings,
  HelpCircle,
  UserPlus,
  LayoutDashboard,
  Users,
  BarChart3,
  Church,
  Send,
  Megaphone,
  Newspaper,
  Baby,
  Clock,
  Briefcase,
  BadgeCheck,
  FileText,
  Upload,
  Plug,
  Shield,
  Building2,
  RefreshCw,
  Keyboard,
  MessageCircle,
  MessageSquarePlus,
  ExternalLink,
  Check,
  UserCog,
  CalendarCheck,
  HeartHandshake,
  ArrowRight,
  Loader2,
} from "lucide-react";
import type { EnhancedSearchResult } from "@/hooks/useEnhancedSearch";
import type { SearchObjectType } from "@/hooks/useGlobalSearch";
import { contributionStatusMap, projectStatusMap, requestStatusMap, taskStatusMap } from "@/lib/status-mapping";

// Comprehensive icon mapping
const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  User,
  Home,
  MessageSquare,
  CheckSquare,
  FolderKanban,
  DollarSign,
  Wallet,
  Package,
  Target,
  HandHeart,
  Calendar,
  Search,
  Navigation,
  Plus,
  Zap,
  Settings,
  HelpCircle,
  UserPlus,
  LayoutDashboard,
  Users,
  BarChart3,
  Church,
  Send,
  Megaphone,
  Newspaper,
  Baby,
  Clock,
  Briefcase,
  BadgeCheck,
  FileText,
  Upload,
  Plug,
  Shield,
  Building2,
  RefreshCw,
  Keyboard,
  MessageCircle,
  MessageSquarePlus,
  ExternalLink,
  Check,
  UserCog,
  CalendarCheck,
  HeartHandshake,
  ArrowRight,
  Loader2,
};

const statusMaps: Record<string, Record<string, { variant?: string; label: string }>> = {
  request: requestStatusMap,
  task: taskStatusMap,
  project: projectStatusMap,
  contribution: contributionStatusMap,
};

interface EnhancedSearchResultItemProps {
  result: EnhancedSearchResult;
  isSelected?: boolean;
  onSelect: (result: EnhancedSearchResult) => void;
  onQuickAction?: (result: EnhancedSearchResult, action: string) => void;
  highlightQuery?: string;
  showQuickActions?: boolean;
}

function toTitleCase(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function highlightText(text: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark className="search-highlight">
        {text.slice(index, index + query.length)}
      </mark>
      {text.slice(index + query.length)}
    </>
  );
}

// Category styling
const categorySemanticMap: Record<string, IconShellSemantic> = {
  request: "requests",
  campaign: "requests",
  task: "tasks",
  asset: "tasks",
  contribution: "finance",
  fund: "finance",
  pledge: "finance",
  navigation: "pages",
  settings: "pages",
  help: "pages",
  person: "pages",
  household: "pages",
  project: "pages",
  event: "pages",
  create: "actions",
  action: "actions",
};

// Quick action definitions per type
const QUICK_ACTIONS: Record<string, Array<{ id: string; icon: string; label: string; tooltip: string }>> = {
  task: [
    { id: "complete", icon: "Check", label: "Complete", tooltip: "Mark as complete" },
    { id: "assign", icon: "UserPlus", label: "Assign", tooltip: "Assign to someone" },
  ],
  request: [
    { id: "assign", icon: "UserPlus", label: "Assign", tooltip: "Assign to someone" },
  ],
  person: [
    { id: "message", icon: "MessageSquare", label: "Message", tooltip: "Send message" },
  ],
  contribution: [
    { id: "assign", icon: "UserPlus", label: "Assign", tooltip: "Assign to someone" },
  ],
  project: [
    { id: "assign", icon: "UserPlus", label: "Assign", tooltip: "Assign to someone" },
  ],
};

export function EnhancedSearchResultItem({
  result,
  isSelected,
  onSelect,
  onQuickAction,
  highlightQuery,
  showQuickActions = true,
}: EnhancedSearchResultItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const Icon = ICONS[result.icon] || Search;
  const semantic = categorySemanticMap[result.category] ?? "neutral";

  // Get status info for records
  const statusMap = result.type === "record" && result.objectType
    ? statusMaps[result.objectType as string]
    : undefined;
  const statusInfo = statusMap && result.status
    ? statusMap[result.status]
    : undefined;

  // Get quick actions for this result type
  const quickActions = result.type === "record" && result.objectType && showQuickActions
    ? QUICK_ACTIONS[result.objectType] || []
    : [];

  const handleQuickAction = async (e: React.MouseEvent, actionId: string) => {
    e.stopPropagation();
    if (onQuickAction && !actionLoading) {
      setActionLoading(actionId);
      try {
        await onQuickAction(result, actionId);
      } finally {
        setActionLoading(null);
      }
    }
  };

  // Render type badge
  const renderTypeBadge = () => {
    if (result.type === "command") {
      const categoryLabels: Record<string, string> = {
        navigation: "Go to",
        create: "Create",
        action: "Action",
        settings: "Setting",
        help: "Help",
      };
      return (
      <span className={cn(
        "text-[10px] uppercase tracking-wider font-medium",
        getIconShellClasses({
          semantic,
          scale: "compact",
          selected: Boolean(isSelected),
          className: "!h-auto !w-auto px-1.5 py-0.5 rounded",
        })
      )}>
        {categoryLabels[result.category] || result.category}
      </span>
      );
    }

    // For records, show the object type
    const typeLabels: Record<string, string> = {
      person: "Person",
      household: "Household",
      request: "Request",
      task: "Task",
      project: "Project",
      contribution: "Contribution",
      fund: "Fund",
      asset: "Asset",
      campaign: "Campaign",
      pledge: "Pledge",
      event: "Event",
    };
    return (
        <span className={cn(
        "text-[10px] uppercase tracking-wider font-medium",
        getIconShellClasses({
          semantic,
          scale: "compact",
          className: "!h-auto !w-auto px-1.5 py-0.5 rounded",
        })
      )}>
        {typeLabels[result.category] || result.category}
      </span>
    );
  };

  return (
    <CommandItem
      value={`${result.type}-${result.id}-${result.label}`}
      onSelect={() => onSelect(result)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        "flex items-center gap-3 py-2.5 px-3 cursor-pointer rounded-xl border transition-all duration-200 group",
        isSelected
          ? "bg-primary-tint border-primary-tint-border shadow-[0_10px_24px_-14px_rgba(205,136,53,0.45)]"
          : "border-border/65 hover:bg-accent/55 hover:border-border/90"
      )}
    >
      {/* Icon */}
      <div className={cn(
        getIconShellClasses({ semantic, selected: Boolean(isSelected), scale: "search" })
      )}>
        <Icon className={getIconGlyphClasses("search")} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground truncate">
            {highlightQuery
              ? highlightText(result.label, highlightQuery)
              : result.label
            }
          </p>
          {renderTypeBadge()}
        </div>
        {result.description && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {highlightQuery
              ? highlightText(result.description, highlightQuery)
              : result.description
            }
          </p>
        )}
      </div>

      {/* Status badge (for records) */}
      {result.status && result.type === "record" && (
        result.objectType === "task" ? (
          <TaskStatusBadge
            status={result.status}
            className="text-xs px-2 py-0.5 flex-shrink-0"
          />
        ) : (
          <StatusBadge
            variant={statusInfo?.variant as "error" | "info" | "neutral" | "success" | "warning" | undefined}
            label={statusInfo?.label ?? toTitleCase(result.status)}
            className="text-xs px-2 py-0.5 flex-shrink-0"
          />
        )
      )}

      {/* Quick actions (shown on hover/select for actionable items) */}
      {quickActions.length > 0 && (isHovered || isSelected) && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {quickActions.map((action) => {
            const ActionIcon = ICONS[action.icon] || Zap;
            const isLoading = actionLoading === action.id;
            return (
                <Tooltip key={action.id} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={getIconShellClasses({
                        semantic: "actions",
                        scale: "compact",
                        className: "h-7 w-7 rounded-md border border-transparent",
                      })}
                      onClick={(e) => handleQuickAction(e, action.id)}
                      disabled={isLoading}
                    >
                    {isLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ActionIcon className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  {action.tooltip}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      )}

      {/* Arrow hint for navigation */}
      {result.type === "command" && (
        <ArrowRight className={cn(
          "h-4 w-4 text-muted-foreground/50 flex-shrink-0 transition-opacity",
          (isHovered || isSelected) ? "opacity-100" : "opacity-0"
        )} />
      )}
    </CommandItem>
  );
}

// Compact version for suggestions
export function CompactSearchResultItem({
  result,
  onSelect,
  highlightQuery,
}: {
  result: EnhancedSearchResult;
  onSelect: (result: EnhancedSearchResult) => void;
  highlightQuery?: string;
}) {
  const Icon = ICONS[result.icon] || Search;
  const semantic = categorySemanticMap[result.category] ?? "neutral";

  return (
    <CommandItem
      value={`compact-${result.type}-${result.id}-${result.label}`}
      onSelect={() => onSelect(result)}
      className={cn(
        "flex items-center gap-2 rounded-lg border border-transparent py-2 px-2 transition-all duration-200",
        "hover:border-border/60 hover:bg-accent/40",
      )}
    >
      <div className={cn(
        getIconShellClasses({ semantic, scale: "compact" })
      )}>
        <Icon className={getIconGlyphClasses("compact")} />
      </div>
      <span className="text-sm truncate flex-1">
        {highlightQuery
          ? highlightText(result.label, highlightQuery)
          : result.label
        }
      </span>
    </CommandItem>
  );
}
